import { ResizeSensor } from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';
import { MosaicKey } from 'react-mosaic-component/lib/types';
import { useDebouncedCallback } from 'use-debounce';

import { TEST_IDS } from 'src/constants/testIds';
import { ProjectDiagramContextMenu } from 'src/containers/ProjectDiagram/components/contextMenu/ProjectDiagramContextMenu';
import { ProjectDiagramTooltip } from 'src/containers/ProjectDiagram/components/tooltip/ProjectDiagramTooltip';
import { useDiagram } from 'src/containers/ProjectDiagram/hooks/useDiagramValue';
import { useTooltip } from 'src/containers/ProjectDiagram/hooks/useTooltipValue';
import { ContextMenuData } from 'src/containers/ProjectDiagram/types/contextMenu';
import { updateGrid } from 'src/containers/ProjectDiagram/utils/grid';
import { entityDrawerSelector } from 'src/selectors/entityDrawer';
import { EntityDrawerType } from 'src/store/ui/types';
import * as copy from 'src/templates/copy';
import { colors } from 'src/templates/styles/colors';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRefCallback } from 'src/utils/hooks/useRefCallback';

type ProjectDiagramContainerProps = {
  // eslint-disable-next-line react/no-unused-prop-types
  id: MosaicKey;
  contextMenu: ContextMenuData | null;
  onContextMenuClose: () => void;
};

export const ProjectDiagramContainer: React.VFC<
  ProjectDiagramContainerProps
> = ({ contextMenu, onContextMenuClose }) => {
  const [tooltip] = useTooltip();
  const diagram = useDiagram();
  const { type: entityDrawerType } = useReduxSelector(entityDrawerSelector);
  const { isOpen: isRuleOverviewDrawerOpen } = useReduxSelector(
    (state) => state.ui.ruleOverviewDrawer
  );

  const isDrawerOpen =
    isRuleOverviewDrawerOpen || entityDrawerType !== EntityDrawerType.NONE;

  const [divRef, setDivRef] = useRefCallback<HTMLDivElement>(null, () => {
    if (diagram && diagram.div === null) {
      diagram.div = divRef.current;
    }
  });

  React.useEffect(() => {
    return () => {
      if (diagram?.div) {
        diagram.div = null;
      }
    };
  }, [diagram, divRef]);

  const handleResize = React.useCallback(() => {
    if (!diagram) {
      return;
    }

    updateGrid(diagram);
  }, [diagram]);

  const debouncedHandleResize = useDebouncedCallback(handleResize, 250);

  return (
    <div
      className="sd-project-diagram-container"
      data-testid={TEST_IDS.projectDiagram.container}
    >
      <div className="sd-project-diagram-container__popovers">
        {tooltip ? (
          <ProjectDiagramTooltip
            isOpen
            rect={tooltip?.rect}
            text={tooltip?.text || ''}
          />
        ) : undefined}
        <ProjectDiagramContextMenu
          contextMenu={contextMenu}
          onClose={onContextMenuClose}
        />
      </div>
      <ResizeSensor onResize={debouncedHandleResize}>
        <div
          ref={setDivRef}
          className={classNames('sd-project-diagram-container__diagram', {
            'sd-project-diagram-container__diagram-narrow': isDrawerOpen,
          })}
        />
      </ResizeSensor>
      {diagram?.model.modelData.draftData ? (
        <div className="sd-project-diagram-container__legend">
          <div
            style={{
              background: colors.draftBackground,
              borderColor: colors.draftOutline,
            }}
          >
            {copy.components.voicebox.legend.changes}
          </div>
          <div
            style={{
              background: colors.deletedBackground,
              borderColor: colors.deletedOutline,
            }}
          >
            {copy.components.voicebox.legend.deletions}
          </div>
        </div>
      ) : null}
    </div>
  );
};
